import {
    isTestMember,
    isVipMember,
    type MemberDataFirestoreRecord,
    type MemberDataRatingScore,
    type OnboardingContentFeedback,
} from "@albertine/types";
import type {
    CardPaymentMethod,
    MemberOnboardingPreferences,
    MemberPaymentPreferences,
    MemberPreferences,
    MemberProactivityItem,
    MemberReferralPromotion,
    MemberStripeAccount,
    MembershipTier,
} from "@albertine/types/openapi/lmt";
import type { AgentPickerItem } from "@prototypes/agent-picker/AgentPicker";
import type {
    FamilyMember,
    FuturePlan,
    PreferenceItem,
    Profile,
    ProfileGroup,
    ScoreRow,
    TopCity,
} from "@prototypes/member-profile/data/profile";
import { format, parseISO } from "date-fns";
import type { Conversation } from "../../common/types/Conversation";
import type { Group } from "../../common/types/Group";
import { isEmailSet, isPhoneNumberSet } from "../../common/utils/member.util";
import { isEmpty } from "../../common/utils/string.util";
import { createMembershipTileState } from "../../utils/members.util";
import { joinDisplayName } from "../../utils/name.util";
import {
    fromTimestampToDate,
    fromTimestampToDateOptional,
    fromTimestampToIsoDateString,
} from "../../utils/timestamp.util";
import {
    formatOnboardingMustKnow,
    formatPushNotifications,
    upcomingDiningPlanInfo,
    upcomingTravelPlanInfo,
} from "./member-profile.util";

export function toProfile({
    member,
    groups,
    agents,
    members,
    membershipTiers,
    onboarding,
    preferences,
    conversation,
    stripe,
    paymentPrefs,
    cards,
    proactivity,
    referrals,
    now = new Date(),
}: {
    member: MemberDataFirestoreRecord;
    groups: Group[];
    agents: AgentPickerItem[];
    members: Record<string, { firstName: string; lastName: string }>;
    membershipTiers: readonly MembershipTier[];
    onboarding?: MemberOnboardingPreferences | null;
    preferences?: MemberPreferences | null;
    conversation?: Conversation;
    stripe?: MemberStripeAccount | null;
    paymentPrefs?: MemberPaymentPreferences | null;
    cards?: CardPaymentMethod[];
    proactivity?: MemberProactivityItem[];
    referrals?: MemberReferralPromotion[];
    now?: Date;
}): Profile {
    return {
        memberId: member.id,
        memberName: joinDisplayName(member.firstName, member.lastName),
        membership: toMembership(
            member,
            groups,
            agents,
            membershipTiers,
            conversation,
            proactivity,
            now,
        ),
        personal: toPersonal(member, onboarding, preferences),
        familyMustKnow: presentText(member.familyMustKnow),
        family: (member.familyMembers ?? []).map((familyMember) =>
            familyMember.type === "limited_profile"
                ? toLimitedFamily(familyMember)
                : toLinkedFamily(familyMember, members),
        ),
        payments: toPayments(paymentPrefs, stripe, cards),
        contact: toContact(member),
        admin: toAdmin(member),
        documents: (member.links ?? []).map((link) => ({
            id: link.id,
            title: link.title,
            url: presentText(link.url),
        })),
        dining: {
            diningPreferences: presentText(member.diningPreferences),
            foodAllergies: presentText(member.foodAllergies),
            ...toDiningFuturePlans(member, onboarding),
        },
        travel: {
            travelMustKnow: presentText(member.travelMustKnow),
            nationality: presentText(member.nationality),
            passportName: presentText(member.passportName),
            passportNumber: presentText(member.passportNumber),
            passportExpiryDate: presentIsoDate(member.passportExpiryDate),
            airlinePreferences: presentText(member.airlinePreferences),
            frequentFlyerNumber: presentText(member.frequentFlyerNumber),
            ...toTravelFuturePlans(member, onboarding),
        },
        events: {
            eventsAndExperiences: presentText(member.eventsAndExperiences),
        },
        wellness: {
            wellnessMustKnow: presentText(member.wellnessMustKnow),
        },
        shopping: {
            luxuryItemsAndCollectibles: presentText(member.luxuryItemsAndCollectibles),
        },
        onboarding: toOnboarding(member, onboarding),
        referrals: referrals
            ? {
                  promotions: referrals.map((item) => ({
                      id: item.id,
                      date: item.startTime,
                      status: item.status,
                      popupContent: item.popupDescription,
                      notificationContent: item.notificationDescription,
                  })),
              }
            : undefined,
        archived: member.archived
            ? {
                  date: member.archivedDate
                      ? fromTimestampToIsoDateString(member.archivedDate)
                      : "",
                  reason: member.archiveReason ?? "",
              }
            : undefined,
        isTestMember: isTestMember(member),
        isVipMember: isVipMember(member),
    };
}

function toMembership(
    member: MemberDataFirestoreRecord,
    groups: Group[],
    agents: AgentPickerItem[],
    membershipTiers: readonly MembershipTier[],
    conversation: Conversation | undefined,
    proactivity: MemberProactivityItem[] | undefined,
    now: Date,
): Profile["membership"] {
    const membership = member.membership;
    const tile = createMembershipTileState(membership, [...membershipTiers], now);
    return {
        tier: membership?.tier,
        trial: tile.trial,
        expired: tile.expired,
        statusNote: membership ? tile.subtitle : undefined,
        trialPeriod: membership?.trial,
        trialEndsOn:
            membership && "endTrialDate" in membership
                ? presentIsoDate(membership.endTrialDate)
                : undefined,
        startsOn: membershipStartsOn(membership),
        endsOn: membership && "endDate" in membership ? membership.endDate : undefined,
        commitmentPeriod:
            membership && "commitmentPeriod" in membership
                ? (membership.commitmentPeriod ?? undefined)
                : undefined,
        billingPeriod:
            membership && "billingPeriod" in membership
                ? (membership.billingPeriod ?? undefined)
                : undefined,
        cancellation: membership?.cancellation
            ? {
                  reason: membership.cancellation.reason,
                  additionalNote: membership.cancellation.additionalNote ?? undefined,
              }
            : undefined,
        assignedTo: assignedTo(member, agents),
        groups: toMemberGroups(member.groups, groups),
        tenant: member.tenant,
        language: member.serviceLanguage ?? undefined,
        lastSeen: member.lastSeenAt
            ? fromTimestampToIsoDateString(member.lastSeenAt)
            : undefined,
        source: member.source ?? undefined,
        additionalSourceInfo: presentText(member.additionalNoteOnSource),
        memberSince: fromTimestampToIsoDateString(member.createdAt),
        proactivity: (proactivity ?? []).slice(0, 5).map((item) => ({
            sharedAt: item.sharedAt,
            messageId: item.messageId,
        })),
        scores: [
            {
                label: "Engagement",
                kind: "engagement",
                lastMessageAt: fromTimestampToDateOptional(
                    conversation?.lastMessageByMemberAt,
                )?.toISOString(),
            },
            scoreRow("Service", member.serviceScore),
            scoreRow("Bookings", member.bookingsScore),
        ].filter((row): row is ScoreRow => row !== undefined),
    };
}

function toPersonal(
    member: MemberDataFirestoreRecord,
    onboarding: MemberOnboardingPreferences | null | undefined,
    preferences: MemberPreferences | null | undefined,
): Profile["personal"] {
    return {
        personalHighlight: presentText(member.personalHighlight),
        personalMustKnow: presentText(member.personalMustKnow),
        title: presentText(member.title),
        firstName: member.firstName,
        lastName: member.lastName,
        greeting: presentText(onboarding?.greetingsName),
        dateOfBirth: presentIsoDate(member.dateOfBirth),
        occupation: presentText(member.occupation),
        memberships: presentText(member.memberships),
        interests: presentText(member.interests),
        loves: presentText(member.loves),
        hates: presentText(member.hates),
        topCities: toTopCities(preferences),
    };
}

function toContact(member: MemberDataFirestoreRecord): Profile["contact"] {
    return {
        email: isEmailSet(member) ? (member.email ?? undefined) : undefined,
        phoneNumber: isPhoneNumberSet(member) ? member.phoneNumber : undefined,
        address: presentText(member.address),
        postalCode: presentText(member.postalCode),
        city: presentText(member.city),
        state: presentText(member.state),
        country: presentText(member.country),
        timeZone: presentText(member.timeZone),
        secondaryEmail: presentText(member.secondaryEmail),
        secondaryPhone: presentText(member.secondaryPhone),
        preferredCommunicationChannel:
            member.preferredCommunicationChannel ?? undefined,
        channelContact: member.channelContact ?? undefined,
        eligibilityDetails: toEligibilityDetails(member.eligibilityDetails),
    };
}

function toAdmin(member: MemberDataFirestoreRecord): Profile["admin"] {
    const philanthropy = toPhilanthropy(member.contributions);

    return {
        iosAppVersion: presentText(member.appVersion),
        iosVersion: presentText(member.iosVersion),
        iosDevice: presentText(member.iosDevice),
        pushNotifications: formatPushNotifications("ios", member),
        androidAppVersion: presentText(member.appVersionAndroid),
        androidVersion: presentText(member.androidVersion),
        androidDevice: presentText(member.androidDevice),
        androidWebNotifications: formatPushNotifications("android/web", member),
        themesPreview: true,
        calendarId: member.calendarId ?? undefined,
        philanthropy,
    };
}

function toPayments(
    paymentPrefs: MemberPaymentPreferences | null | undefined,
    stripe: MemberStripeAccount | null | undefined,
    cards: CardPaymentMethod[] | undefined,
): Profile["payments"] {
    return {
        paymentsMustKnow: presentText(paymentPrefs?.paymentsMustKnow),
        preferredCurrency: presentText(paymentPrefs?.preferredCurrency),
        stripe: stripe
            ? {
                  id: stripe.stripeCustomerId,
                  name: stripe.name ?? undefined,
                  email: stripe.email ?? undefined,
              }
            : undefined,
        cards: cards?.map((card) => ({
            brand: card.brand,
            last4: card.last4,
            expMonth: card.expMonth,
            expYear: card.expYear,
        })),
        invoiceNameAliases: paymentPrefs?.invoiceNameAliases,
    };
}

function toOnboarding(
    member: MemberDataFirestoreRecord,
    onboarding: MemberOnboardingPreferences | null | undefined,
): Profile["onboarding"] {
    const benefits = onboarding?.benefits?.map(toPreferenceItem);
    const proactivity = onboarding?.recommendations?.map(toPreferenceItem);
    const summary = formatOnboardingMustKnow(
        onboarding?.hasUsedConcierge,
        onboarding?.hasUsedConciergeFor,
    );
    return {
        summary,
        benefits: benefits?.length ? benefits : undefined,
        proactivity: proactivity?.length ? proactivity : undefined,
        completedVia: member.onboardingCompletedVia ?? undefined,
        completedAt: member.onboardingCompletedAt
            ? fromTimestampToIsoDateString(member.onboardingCompletedAt)
            : undefined,
        hasLoggedIn: member.hasLoggedIn === true,
    };
}

function toLimitedFamily(
    familyMember: Extract<
        NonNullable<MemberDataFirestoreRecord["familyMembers"]>[number],
        { type: "limited_profile" }
    >,
): FamilyMember {
    return {
        id: familyMember.id,
        kind: "limited",
        name: `${familyMember.firstName} ${familyMember.lastName}`,
        detail: familyMember.relationship,
        firstName: familyMember.firstName,
        lastName: familyMember.lastName,
        mustKnow: presentText(familyMember.mustKnow),
        dateOfBirth: presentIsoDate(familyMember.dateOfBirth),
        nationality: presentText(familyMember.nationality),
        passportName: presentText(familyMember.passportName),
        passportNumber: presentText(familyMember.passportNumber),
        passportExpiryDate: presentIsoDate(familyMember.passportExpiryDate),
        frequentFlyerNumber: presentText(familyMember.frequentFlyerNumber),
    };
}

function toLinkedFamily(
    familyMember: Extract<
        NonNullable<MemberDataFirestoreRecord["familyMembers"]>[number],
        { type: "linked_profile" }
    >,
    members: Record<string, { firstName: string; lastName: string }>,
): FamilyMember {
    const linked = members[familyMember.member];

    return {
        id: familyMember.id,
        kind: "linked",
        name: linked ? `${linked.firstName} ${linked.lastName}` : familyMember.member,
        detail: "Dual membership",
        linkedMemberId: familyMember.member,
    };
}

function toPhilanthropy(
    contributions: MemberDataFirestoreRecord["contributions"],
): Profile["admin"]["philanthropy"] {
    if (!contributions) return undefined;
    const { trees, kilograms, squareMeters } = contributions;
    if (!trees && !kilograms && !squareMeters) return undefined;
    return { trees, kilograms, squareMeters };
}

function toDiningFuturePlans(
    member: MemberDataFirestoreRecord,
    onboarding: MemberOnboardingPreferences | null | undefined,
): Pick<Profile["dining"], "futurePlan" | "onboardingFuturePlan"> {
    return {
        futurePlan: toMemberFuturePlan(member.diningFuturePlans),
        onboardingFuturePlan:
            onboarding?.anyUpcomingDiningPlans && onboarding.createdAt
                ? {
                      text: upcomingDiningPlanInfo(
                          onboarding.anyUpcomingDiningPlans,
                          onboarding.upcomingDiningQuery || undefined,
                      ),
                      source: planSource("Onboarding", parseISO(onboarding.createdAt)),
                  }
                : undefined,
    };
}

function toTravelFuturePlans(
    member: MemberDataFirestoreRecord,
    onboarding: MemberOnboardingPreferences | null | undefined,
): Pick<Profile["travel"], "futurePlan" | "onboardingFuturePlan"> {
    return {
        futurePlan: toMemberFuturePlan(member.travelFuturePlans),
        onboardingFuturePlan:
            onboarding?.anyUpcomingTravelPlans && onboarding.createdAt
                ? {
                      text: upcomingTravelPlanInfo(
                          onboarding.anyUpcomingTravelPlans,
                          onboarding.upcomingTravelQuery || undefined,
                      ),
                      source: planSource("Onboarding", parseISO(onboarding.createdAt)),
                  }
                : undefined,
    };
}

function toMemberFuturePlan(
    plan:
        | {
              plans: string;
              updatedName: string;
              updatedAt: MemberDataFirestoreRecord["createdAt"];
          }
        | null
        | undefined,
): FuturePlan | undefined {
    if (!plan || isEmpty(plan.plans)) return undefined;
    return {
        text: plan.plans,
        source: planSource(plan.updatedName, fromTimestampToDate(plan.updatedAt)),
    };
}

function planSource(
    name: string | undefined,
    at: Date | undefined,
): string | undefined {
    if (!at || Number.isNaN(at.getTime())) return name;
    const when = format(at, "d MMM, HH:mm");
    return name ? `${name} · ${when}` : when;
}

function toTopCities(
    preferences: MemberPreferences | null | undefined,
): TopCity[] | undefined {
    const cities = preferences?.topCities
        ?.map((city) =>
            city.city
                ? {
                      city: city.city,
                      country: city.country,
                      countryCode: city.countryCode,
                  }
                : undefined,
        )
        .filter((city): city is TopCity => city !== undefined);

    return cities?.length ? cities : undefined;
}

function toMemberGroups(
    groupIds: string[] | null | undefined,
    groups: Group[],
): ProfileGroup[] {
    const groupsById = new Map(groups.map((group) => [group.id, group]));
    return (groupIds ?? []).map((id) => ({
        id,
        name: groupsById.get(id)?.displayName || id,
    }));
}

function assignedTo(
    member: MemberDataFirestoreRecord,
    agents: AgentPickerItem[],
): Profile["membership"]["assignedTo"] {
    const id = member.assignedTo;
    if (!id) {
        return { id: "", name: member.assignedToName ?? "Unassigned" };
    }

    const agent = agents.find((item) => item.id === id);
    return {
        id,
        name: agent?.name ?? member.assignedToName ?? id,
    };
}

function membershipStartsOn(
    membership: MemberDataFirestoreRecord["membership"],
): string | undefined {
    if (!membership) return undefined;
    if ("startTrialDate" in membership) {
        return presentIsoDate(membership.startTrialDate);
    }
    if ("startDate" in membership) {
        return membership.startDate;
    }
    return undefined;
}

function scoreRow(
    label: string,
    score: MemberDataRatingScore | null | undefined,
): ScoreRow | undefined {
    if (!score) return undefined;
    return {
        label,
        kind: "rating",
        average: score.average,
        ratingsCount: score.ratingsCount ?? 0,
    };
}

function toPreferenceItem(item: {
    title: string;
    description?: string | null;
    feedback: OnboardingContentFeedback;
}): PreferenceItem {
    return {
        title: item.title,
        description: item.description ?? "",
        sentiment: toSentiment(item.feedback),
    };
}

function toSentiment(feedback: OnboardingContentFeedback): PreferenceItem["sentiment"] {
    switch (feedback) {
        case "like":
            return "up";
        case "heart":
            return "love";
        case "dislike":
            return "down";
        case "neutral":
            return "up";
        default: {
            const _exhaustive: never = feedback;
            return _exhaustive;
        }
    }
}

function toEligibilityDetails(
    details: MemberDataFirestoreRecord["eligibilityDetails"],
): Profile["contact"]["eligibilityDetails"] {
    if (!details) return undefined;

    const bin = presentText(details.bin);
    const last4 = presentText(details.last4);
    if (!bin && !last4) return undefined;

    return { bin, last4 };
}

function presentText(value: string | null | undefined): string | undefined {
    if (!value || isEmpty(value)) return undefined;
    return value;
}

function presentIsoDate(value: string | null | undefined): string | undefined {
    if (!value || isEmpty(value)) return undefined;
    return value;
}

export function toProfileGroups(groups: Group[]): ProfileGroup[] {
    return groups.map((group) => ({
        id: group.id,
        name: group.displayName,
    }));
}
